import React, { useEffect, useState, useCallback } from "react";
import { apiGet } from "../../utils/apiClient";

interface AuthLog {
  timestamp: string;
  event_type: string;
  tenant_id: string;
  uid: string | null;
  email: string | null;
  provider: string | null;
  ip_address: string;
  user_agent: string;
  shop_url: string | null;
  role: string | null;
  plan: string | null;
  trial_status: string | null;
  error_code: string | null;
  error_message: string | null;
}

interface ApiResponse {
  ok: boolean;
  rows?: AuthLog[];
  total?: number;
  limit?: number;
  offset?: number;
  days?: number;
  error?: string;
}

const thBase = "border px-2 py-1 whitespace-nowrap text-[11px] leading-tight font-semibold";
const tdBase = "border px-2 py-1 text-left align-top text-[11px] leading-tight";

function fmtDateTime(v?: string | null) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("ja-JP");
}

function EventBadge({ type }: { type: string }) {
  const isLogin = type === "login";
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${
        isLogin ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
      }`}
    >
      {type}
    </span>
  );
}

export default function SuperAuthLogsPage() {
  const [logs, setLogs] = useState<AuthLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // フィルター
  const [days, setDays] = useState(7);
  const [eventType, setEventType] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [email, setEmail] = useState("");
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.append("days", String(days));
      params.append("limit", String(limit));
      params.append("offset", String(offset));
      if (eventType) params.append("eventType", eventType);
      if (tenantId) params.append("tenantId", tenantId);
      if (email) params.append("email", email);

      const res = await apiGet(`/api/super/auth-logs?${params.toString()}`);
      if (!res.ok) throw new Error("API request failed");

      const data: ApiResponse = await res.json();
      if (!data.ok) throw new Error(data.error || "API error");

      setLogs(data.rows || []);
      setTotal(data.total || 0);
    } catch (e: any) {
      setError(e?.message ?? "ログの取得に失敗しました");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [days, eventType, tenantId, email, limit, offset]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="w-full max-w-none min-w-0 h-full flex flex-col gap-2 px-2 py-2 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-base font-bold">ログイン/ログアウト履歴</h1>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-[12px] px-2 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? "読込中..." : "再読み込み"}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="px-2 py-1 bg-red-50 border border-red-200 text-red-700 text-[12px] rounded">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 p-2 bg-gray-50 border rounded">
        <div className="flex flex-col gap-0.5">
          <label className="text-[11px] font-semibold text-gray-600">期間（日数）</label>
          <input
            type="number"
            value={days}
            onChange={(e) => setDays(Math.max(1, parseInt(e.target.value) || 1))}
            min={1}
            className="px-1.5 py-0.5 border rounded text-[11px] w-16"
          />
        </div>

        <div className="flex flex-col gap-0.5">
          <label className="text-[11px] font-semibold text-gray-600">イベント</label>
          <select
            value={eventType}
            onChange={(e) => {
              setEventType(e.target.value);
              setOffset(0);
            }}
            className="px-1.5 py-0.5 border rounded text-[11px]"
          >
            <option value="">すべて</option>
            <option value="login">ログイン</option>
            <option value="logout">ログアウト</option>
          </select>
        </div>

        <div className="flex flex-col gap-0.5">
          <label className="text-[11px] font-semibold text-gray-600">テナント</label>
          <input
            type="text"
            value={tenantId}
            onChange={(e) => {
              setTenantId(e.target.value);
              setOffset(0);
            }}
            placeholder="tenant_id"
            className="px-1.5 py-0.5 border rounded text-[11px] w-32"
          />
        </div>

        <div className="flex flex-col gap-0.5">
          <label className="text-[11px] font-semibold text-gray-600">メール</label>
          <input
            type="text"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setOffset(0);
            }}
            placeholder="email"
            className="px-1.5 py-0.5 border rounded text-[11px] w-40"
          />
        </div>

        <div className="flex flex-col gap-0.5">
          <label className="text-[11px] font-semibold text-gray-600">件数</label>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(parseInt(e.target.value));
              setOffset(0);
            }}
            className="px-1.5 py-0.5 border rounded text-[11px]"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
          </select>
        </div>
      </div>

      {/* Info */}
      <div className="text-[11px] text-gray-600">
        合計: <span className="font-semibold">{total.toLocaleString()}</span> 件 |
        表示: <span className="font-semibold">{offset + 1}</span> ～{" "}
        <span className="font-semibold">{Math.min(offset + limit, total).toLocaleString()}</span>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-1">
        <div className="flex-1 min-h-0 overflow-auto">
          <table className="border-collapse">
            <thead className="sticky top-0 bg-gray-100">
              <tr>
                <th className={`${thBase} sticky left-0 z-10 bg-gray-100`}>日時</th>
                <th className={thBase}>イベント</th>
                <th className={thBase}>テナント</th>
                <th className={thBase}>メール</th>
                <th className={thBase}>UID</th>
                <th className={thBase}>IPアドレス</th>
                <th className={thBase}>User-Agent</th>
                <th className={thBase}>プロバイダ</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={8} className={`${tdBase} text-center py-4`}>
                    {loading ? "読込中..." : "ログがありません"}
                  </td>
                </tr>
              ) : (
                logs.map((log, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className={`${tdBase} sticky left-0 z-10 bg-white hover:bg-gray-50 min-w-fit`}>
                      {fmtDateTime(log.timestamp)}
                    </td>
                    <td className={tdBase}>
                      <EventBadge type={log.event_type} />
                    </td>
                    <td className={`${tdBase} font-mono text-[10px]`}>{log.tenant_id}</td>
                    <td className={tdBase}>{log.email || "-"}</td>
                    <td className={`${tdBase} font-mono text-[10px] min-w-48`} title={log.uid || undefined}>
                      {log.uid || "-"}
                    </td>
                    <td className={`${tdBase} font-mono text-[10px]`}>{log.ip_address}</td>
                    <td className={`${tdBase} font-mono text-[10px] min-w-96`} title={log.user_agent}>
                      {log.user_agent}
                    </td>
                    <td className={tdBase}>{log.provider || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between gap-2 p-2 bg-gray-50 border rounded">
        <div className="text-[11px] text-gray-600">
          ページ: <span className="font-semibold">{Math.floor(offset / limit) + 1}</span>
        </div>
        <div className="flex gap-1">
          <button
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0 || loading}
            className="text-[11px] px-2 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            ← 前へ
          </button>
          <button
            onClick={() => setOffset(offset + limit)}
            disabled={offset + limit >= total || loading}
            className="text-[11px] px-2 py-1 border rounded bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            次へ →
          </button>
        </div>
      </div>
    </div>
  );
}
